import { useState } from 'react'
import { useQueries, UseQueryOptions } from 'react-query'
import { useParams } from 'react-router-dom'

import Modal, { Size } from '@/components/Modal'
import ButtonModal from '@/components/TailwindUI/ButtonModal'
import Checkbox from '@/components/TailwindUI/Checkbox'
import ModalVariantLogo from '@/components/TailwindUI/ModalVariantLogo'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { WorkgridCatalogDatasource, WorkgridDatasource } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import { dataSourceV3CatalogQueryOptions, dataSourceV3QueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import cn from '@/utilities/cn'

type Props = {
  size?: Size
  variant: 'success' | 'warning' | 'error' | 'info'
  isOpen: boolean
  onConfirm: () => void
  onSecondaryConfirm?: () => void
  onCancel: () => void
  descriptor?: string
  confirmText?: string
  secondaryText?: string
  cancelText?: string
  loadingText?: string
  loading?: boolean
  disabled?: boolean
  showCancel?: boolean
  appDataSources?: Array<WorkgridDatasource>
  currentUserId?: string
}

const ConfirmDialogWithCheckBox = ({
  size = 'xs',
  variant,
  isOpen,
  onConfirm,
  onSecondaryConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loadingText = 'Loading...',
  loading = false,
  disabled = false,
  secondaryText,
  showCancel = true,
  appDataSources
}: Props) => {
  const [isChecked, setIsChecked] = useState(false)
  const featureFlags = useFeatures()
  const agentsFlagOn = !!featureFlags[WORKSHOP_AGENTS]
  const [error, setError] = useState('')
  const { spaceId } = useParams<{ spaceId: string }>()
  type WorkgridDatasourceInstanceQueries = UseQueryOptions<WorkgridDatasource>[]
  const dataSourceInstancesQuery = useQueries<WorkgridDatasourceInstanceQueries>(
    appDataSources?.map(ds => ({
      ...dataSourceV3QueryOptions({
        spaceId,
        datasourceId: ds.datasourceId,
        instanceId: ds.id
      }),
      enabled: !!appDataSources
    })) || []
  )
  type WorkgridDatasourceQueries = UseQueryOptions<WorkgridCatalogDatasource>[]
  const datasourcesQueryResult = useQueries<WorkgridDatasourceQueries>(
    appDataSources?.map(ds => ({
      ...dataSourceV3CatalogQueryOptions({ spaceId, datasourceId: ds.datasourceId })
    })) || []
  )
  const hasCheckBox = dataSourceInstancesQuery?.some(dataSource => dataSource.data?.configuration.auth !== 'oauth2')

  return (
    <Modal size={size} isOpen={isOpen} onClose={() => onCancel()}>
      <div className="px-4 py-3">
        <div className="sm:flex sm:items-start">
          <ModalVariantLogo variant={variant} />
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <h3 className="text-base font-semibold leading-6 text-zinc-900">
              {`Owners added to this ${agentsFlagOn ? 'agent' : 'app'} can access data source info`}
            </h3>
            <div className="mt-2">
              <p className="text-sm text-zinc-500">
                Owners of this {agentsFlagOn ? 'agent' : 'app'} will have access to edit, test, and deploy against the
                datasource instances used.
              </p>
            </div>
            {dataSourceInstancesQuery?.map((dataSource, index) => {
              if (dataSource.data?.configuration.auth !== 'oauth2') {
                const datasourceDetails = datasourcesQueryResult.find(
                  ds => ds.data?.id === dataSource.data?.datasourceId
                )?.data
                return (
                  <div className="mb-4 mt-4 flex min-w-0 flex-auto space-x-4" key={index}>
                    <img className="h-10 w-10 rounded-md" src={datasourceDetails?.iconUrl} alt="datasource icon" />
                    <div className="min-w-0 flex-auto">
                      <div>
                        <h2 className="text-sm text-zinc-900">{datasourceDetails?.name}</h2>
                        <p className="text-sm text-zinc-500">{dataSource.data?.name}</p>
                      </div>
                    </div>
                  </div>
                )
              }
            })}

            {dataSourceInstancesQuery?.some(dataSource => dataSource.data?.configuration.auth === 'oauth2') && (
              <div className="mt-2 mb-2">
                <p className="text-sm text-zinc-500">
                  {'OAuth data source information will only be seen by the authenticated user of that instance.'}
                </p>
              </div>
            )}
            {hasCheckBox && (
              <>
                <Checkbox
                  checked={isChecked}
                  onChange={e => setIsChecked(e.target.checked)}
                  label={`I understand that owners have access to data sources via the ${
                    agentsFlagOn ? 'agent' : 'app'
                  }.`}
                />
                {error && <p style={{ color: 'red' }}>{error}</p>}
              </>
            )}
          </div>
        </div>
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <ButtonModal
            disabled={disabled || loading}
            variant={variant}
            onConfirm={() => {
              if (hasCheckBox && !isChecked) {
                setError('Please confirm that you understand app owner access.')
              } else {
                onConfirm()
                setIsChecked(false)
                setError('')
              }
            }}
            loadingText={loadingText}
            confirmText={confirmText}
          />
          {secondaryText && onSecondaryConfirm && (
            <ButtonModal
              disabled={disabled || loading}
              variant={variant}
              onConfirm={() => {
                onSecondaryConfirm()
                setIsChecked(false)
                setError('')
              }}
              loadingText={loadingText}
              confirmText={secondaryText}
            />
          )}
          {showCancel && (
            <button
              type="button"
              disabled={loading}
              className={cn(
                'mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 sm:mt-0 sm:w-auto'
              )}
              onClick={() => {
                onCancel()
                setIsChecked(false)
                setError('')
              }}
            >
              {cancelText}
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ConfirmDialogWithCheckBox
